import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';

import { Button } from '@salesforce/design-system-react';

import { FormattedHeading } from '../../../i18n/components/';
import { AttributeMappingEditor } from '../';

import { FlagContext } from '../../../flags/Flags';
import { withSession } from '../../../i18n/containers/AppInit.js';

import './AttributeMappingPanel.scss';

class AttributeMappingPanel extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showNewMappingRow: false
    };
  }

  updateRules(update, replaceAll) {
    const classifier = replaceAll
      ? { ...update }
      : {
          ...this.props.rules.classifier,
          ...update
        };
    this.props.actions.updateRules({
      ...this.props.rules,
      classifier
    });
  }

  saveRules(session) {
    return () => {
      const { classifier } = this.props.rules;
      this.props.actions.saveRules(
        {
          ...this.props.rules,
          classifier
        },
        { ruleType: 'classifier', cToken: session.cToken }
      );
    };
  }

  addNewMapping = () => {
    this.setState({
      showNewMappingRow: true
    });
  };

  removeNewMapping = () => {
    this.setState({
      showNewMappingRow: false
    });
  };

  deleteRule = assetKey => {
    if (!assetKey) {
      this.removeNewMapping();
      return;
    }

    const update = { ...this.props.rules.classifier };
    delete update[assetKey];

    this.updateRules(update, true);
  };

  dirty() {
    return (
      JSON.stringify(this.props.rules.classifier) !==
      JSON.stringify(this.props.loadedRules.classifier)
    );
  }

  validateRules() {
    const { classifier } = this.props.rules;
    return !Object.values(classifier).find(value => !value.consumerKey);
  }

  newMappingAllowed(flags) {
    const rulesAreValid = this.validateRules();
    return (
      !(flags && flags.isReadonly) &&
      rulesAreValid &&
      !this.state.showNewMappingRow
    );
  }

  saveAllowed(flags) {
    const rulesAreValid = this.validateRules();
    return (
      !(flags && flags.isReadonly) &&
      this.dirty() &&
      rulesAreValid &&
      !this.state.showNewMappingRow
    );
  }

  render() {
    const { formatMessage } = this.props.intl;
    const saveRuleLabel = formatMessage({
      id: 'components.ecs.rules.save'
    });
    const cancelRuleLabel = formatMessage({
      id: 'components.ecs.rules.cancel'
    });

    return withSession(session => {
      return (
        <FlagContext.Consumer>
          {flags => (
            <div className="slds-grid slds-wrap">
              <div className="slds-col attribute-mapping-panel">
                <div className="attribute-mapping-panel-header">
                  <p>
                    <FormattedMessage id="components.ecs.rules.mapping.description" />
                  </p>
                  <FormattedHeading id="components.ecs.rules.mapping.heading" />
                </div>
                <AttributeMappingEditor
                  actions={this.props.actions}
                  assetKeys={this.props.assetKeys}
                  profileKeys={this.props.profileKeys}
                  mappingRules={this.props.rules.classifier}
                  onRuleChange={this.updateRules.bind(this)}
                  onAddNewMapping={this.addNewMapping}
                  onDeleteRule={this.deleteRule}
                  showNewMappingRow={this.state.showNewMappingRow}
                  canAddMapping={this.newMappingAllowed(flags)}
                />
                <div className="footer-button-actions">
                  <Button
                    label={cancelRuleLabel}
                    disabled={!this.saveAllowed(flags)}
                    variant="neutral"
                    onClick={this.props.openUnsavedChangesModal}
                  />
                  <Button
                    id="mappingRulesSaveBtn"
                    label={saveRuleLabel}
                    disabled={!this.saveAllowed(flags)}
                    variant="brand"
                    onClick={this.saveRules(session)}
                  />
                </div>
              </div>
            </div>
          )}
        </FlagContext.Consumer>
      );
    });
  }
}

AttributeMappingPanel.propTypes = {
  actions: PropTypes.object,
  assetKeys: PropTypes.array,
  profileKeys: PropTypes.array,
  rules: PropTypes.object,
  loadedRules: PropTypes.object,
  history: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(AttributeMappingPanel);
